/**
 * Serialize the rich composer's DOM into the plain text the send lanes carry.
 * Lists become their typed form ("1. " / "- " prefixes, ol start honored) so
 * the server sees exactly what the user would have typed by hand — the rich
 * editor is a nicer pen, never a different message.
 */
export function serializeComposer(root: HTMLElement): string {
  const lines: string[] = [];
  let current = "";
  let dirty = false;
  const flush = () => {
    lines.push(current);
    current = "";
    dirty = false;
  };

  const walkInline = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? "";
      dirty = true;
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === "BR") {
      flush();
      return;
    }
    node.childNodes.forEach(walkInline);
  };

  const walkBlock = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? "";
      dirty = true;
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    const tag = node.tagName;
    if (tag === "BR") {
      flush();
      return;
    }
    if (tag === "OL" || tag === "UL") {
      if (dirty) flush();
      const start =
        tag === "OL" ? parseInt(node.getAttribute("start") ?? "1", 10) || 1 : 0;
      let i = 0;
      for (const li of Array.from(node.children)) {
        if (li.tagName !== "LI") continue;
        current = tag === "OL" ? `${start + i}. ` : "- ";
        dirty = true;
        li.childNodes.forEach(walkInline);
        if (dirty) flush();
        i += 1;
      }
      return;
    }
    if (tag === "DIV" || tag === "P") {
      if (dirty) flush();
      // Chrome's empty line is <div><br></div> — one blank line, not two.
      if (
        node.childNodes.length === 1 &&
        node.firstChild instanceof HTMLElement &&
        node.firstChild.tagName === "BR"
      ) {
        lines.push("");
        return;
      }
      node.childNodes.forEach(walkInline);
      if (dirty) flush();
      return;
    }
    node.childNodes.forEach(walkInline);
  };

  root.childNodes.forEach(walkBlock);
  if (dirty) flush();
  // Trailing empties are editing artifacts, not message content.
  while (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines.join("\n");
}
